/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Licensing figures for customer-facing screens.
 *
 * AU-1 (FCR S557). A QLM licence stores one more activation seat than the
 * customer bought. The extra seat is an internal reinstall buffer. It must
 * never be displayed and must never grant an activation.
 *
 * The conversion is done on the server, in the function that assembles the
 * licence record, and arrives as `PurchasedQuantity`. This module is the one
 * place the browser reads it, so that no screen computes the figure for
 * itself -- that is how the same fault previously reached four screens and
 * the activation gate.
 *
 * The fallback exists only for licence objects that did not come through the
 * licences endpoint (WebSocket payloads and cached records written before
 * this field existed). It applies the same rule the server applies.
 */

import i18n from "@/i18n";

/** The shape this module needs. Any licence record satisfies it. */
export interface LicenseSeatFields {
  /** Purchased quantity, produced by the server. Preferred source. */
  PurchasedQuantity?: number | null;
  /** Raw QLM seat count -- purchased quantity plus the reinstall buffer. */
  MaxActivations?: number | null;
  /** Legacy QLM field carrying the same raw seat count. */
  NumLicenses?: number | null;
  /** Devices currently activated. */
  ActivationCount?: number | null;
}

/**
 * The number of devices the customer paid for.
 *
 * Returns 0 for an expired licence, which entitles nothing. Otherwise never
 * returns less than 1: a live licence always covers at least one device, and
 * legacy records that pre-date the reinstall buffer store the purchased
 * quantity directly.
 */
export function getPurchasedDeviceCount(
  license: LicenseSeatFields | null | undefined,
): number {
  if (!license) return 0;

  // Server-produced value wins. It is authoritative including the expired
  // case, where it is deliberately 0.
  const provided = license.PurchasedQuantity;
  if (typeof provided === "number" && Number.isFinite(provided)) {
    return Math.max(0, Math.trunc(provided));
  }

  const rawSeats = license.MaxActivations ?? license.NumLicenses;

  // No seat data at all is missing information, not an absence of entitlement.
  // A live licence always covers at least one device, so 1 is the safe default.
  // This is deliberately different from a reported seat count of 0, below.
  if (typeof rawSeats !== "number" || !Number.isFinite(rawSeats)) return 1;

  // A reported count of 0 means the licence is expired and entitles nothing.
  // Subtracting the buffer from it must not produce a negative figure on screen.
  if (rawSeats <= 0) return 0;

  return Math.max(1, Math.trunc(rawSeats) - 1);
}

/** Devices currently activated, floored at 0. */
export function getActivatedDeviceCount(
  license: LicenseSeatFields | null | undefined,
  fallbackCount = 0,
): number {
  const count = license?.ActivationCount;
  if (typeof count === "number" && Number.isFinite(count)) {
    return Math.max(0, Math.trunc(count));
  }
  return Math.max(0, Math.trunc(fallbackCount));
}

/**
 * Devices the customer can still activate.
 *
 * This is the activation gate as well as a display figure. Comparing against
 * the raw seat count here is what allowed a one-seat purchase to be activated
 * on two machines.
 */
export function getAvailableDeviceCount(
  license: LicenseSeatFields | null | undefined,
  fallbackActivationCount = 0,
): number {
  const purchased = getPurchasedDeviceCount(license);
  const used = getActivatedDeviceCount(license, fallbackActivationCount);
  return Math.max(0, purchased - used);
}

/**
 * U-1 (FCR S557): one phrasing for this figure on every screen.
 *
 * Example: "1 of 2 devices activated".
 */
export function formatDeviceUsage(
  activated: number,
  purchased: number,
): string {
  const used = Math.max(0, Math.trunc(activated));
  const total = Math.max(0, Math.trunc(purchased));
  // PH-3 (S637): one interpolated key carries the WHOLE sentence, with the
  // device noun passed in as a value.
  //
  // English needs one plural rule for "device". German, French, Italian,
  // Spanish and Portuguese each need their own, and the word order differs --
  // so splitting this into "{{used}} of {{total}}" plus a separate noun would
  // force the translator to guess where the noun goes. The six translations
  // were written for exactly this design, which is why the keys already carry
  // the noun as its own value.
  return i18n.t("dashboard.licenseDetails.devices.usageFormat", {
    used,
    total,
    deviceLabel:
      total === 1
        ? i18n.t("dashboard.licenseDetails.devices.deviceOne")
        : i18n.t("dashboard.licenseDetails.devices.deviceMany"),
  });
}
